import DashboardIcon from '@mui/icons-material/Dashboard';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import InsightsIcon from '@mui/icons-material/Insights';
import BookIcon from '@mui/icons-material/Book';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import GroupsIcon from '@mui/icons-material/Groups';
import GridViewIcon from '@mui/icons-material/GridView';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import { useSelector } from 'react-redux';
import EventIcon from '@mui/icons-material/Event';
import ForumIcon from '@mui/icons-material/Forum';
import AirplaneTicketIcon from '@mui/icons-material/AirplaneTicket';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import { useFinanceAccess } from './useFinance';
import AdmissionsBadge from '../components/admissions/AdmissionsBadge';
import {
  canAddJobs,
  canManageAdmissions,
  canManageForms,
  canManageJobDigest,
  canManageSuperStudents,
  canSendSmsCampaign,
  canViewStudentIntelligence,
} from '../utils/permissionKeys';

const BULK_SMS_ITEM = {
  id: 31,
  title: 'Bulk SMS',
  icon: <SmsOutlinedIcon />,
  link: '/sms',
};

const JOBS_ITEM = {
  id: 32,
  title: 'Jobs for Graduates',
  icon: <WorkOutlineIcon />,
  link: '/jobs',
};

const ADMISSIONS_ITEM = {
  id: 33,
  title: 'Admissions',
  icon: <HowToRegOutlinedIcon />,
  link: '/admissions',
  badge: <AdmissionsBadge />,
};

const FORMS_ITEM = {
  id: 36,
  title: 'Forms',
  icon: <AssignmentTurnedInOutlinedIcon />,
  link: '/forms',
};

// Every administrator submits their own invoices.
const MY_INVOICES_ITEM = {
  id: 34,
  title: 'My invoices',
  icon: <ReceiptLongOutlinedIcon />,
  link: '/my-invoices',
};

// Only finance holders (the grant list, not a role) see everyone's.
const FINANCE_ITEM = {
  id: 35,
  title: 'Consultant invoices',
  icon: <AccountBalanceOutlinedIcon />,
  link: '/finance/invoices',
};

// Insert item after the entry linking to `after`, or at the end.
function insertAfter(items, after, item) {
  const index = items.findIndex((entry) => entry.link === after);
  if (index === -1) return [...items, item];
  return [...items.slice(0, index + 1), item, ...items.slice(index + 1)];
}

// Bulk SMS, Jobs and Admissions are permission-based for every role, so they
// are added here once: Bulk SMS after Bulk Email, Jobs after the Impact
// Report, Admissions after Cohorts.
function withPermissionItems(items) {
  return insertAfter(
    insertAfter(
      insertAfter(items, '/emails', BULK_SMS_ITEM),
      '/impact',
      JOBS_ITEM
    ),
    '/cohorts',
    ADMISSIONS_ITEM
  ).flatMap((item) =>
    item.link === '/admissions' ? [item, FORMS_ITEM] : [item]
  );
}

function filterSidebarByPermissions(items, permissions, role, financeHolder) {
  return [
    ...withPermissionItems(items),
    MY_INVOICES_ITEM,
    ...(financeHolder ? [FINANCE_ITEM] : []),
  ].filter((item) => {
    if (item.link === '/sms') {
      return canSendSmsCampaign(permissions, role);
    }
    if (item.link === '/jobs') {
      return (
        canManageJobDigest(permissions, role) || canAddJobs(permissions, role)
      );
    }
    if (item.link === '/admissions') {
      return canManageAdmissions(permissions, role);
    }
    if (item.link === '/forms') {
      return canManageForms(permissions, role);
    }
    if (item.link === '/student-intelligence') {
      return canViewStudentIntelligence(permissions, role);
    }
    if (item.link === '/super-students') {
      return canManageSuperStudents(permissions, role);
    }
    return true;
  });
}

const useSidebarData = () => {
  const role = useSelector((state) => state.auth.userDetails.role);
  const permissions = useSelector((state) => state.auth.permissions ?? []);
  const { data: financeHolder } = useFinanceAccess();

  switch (role) {
    case 'Admin':
      return filterSidebarByPermissions(
        [
          { id: 1, title: 'Dashboard', icon: <DashboardIcon />, link: '/' },
          {
            id: 2,
            title: 'Analytics',
            icon: <AnalyticsIcon />,
            link: '/analytics',
          },
          {
            id: 30,
            title: 'Impact Report',
            icon: <InsightsIcon />,
            link: '/impact',
          },
          {
            id: 22,
            title: 'Student Intel',
            icon: <PersonSearchIcon />,
            link: '/student-intelligence',
          },
          {
            id: 23,
            title: 'Administrators',
            icon: <PersonAddAltIcon />,
            link: '/users',
          },
          {
            id: 24,
            title: 'Roles & Permissions',
            icon: <ManageAccountsOutlinedIcon />,
            link: '/roles&permissions',
          },
          {
            id: 5,
            title: 'Cohorts',
            icon: <GridViewIcon />,
            link: '/cohorts',
          },
          {
            id: 2001,
            title: 'Super Students',
            icon: <AutoAwesomeIcon />,
            link: '/super-students',
          },
          // {
          //   id: 6,
          //   title: 'Paid Cohorts',
          //   icon: <GridViewIcon />,
          //   link: '/paid-cohorts',
          // },
          { id: 8, title: 'Groups', icon: <GroupsIcon />, link: '/groups' },
          { id: 9, title: 'Courses', icon: <BookIcon />, link: '/courses' },
          {
            id: 10,
            title: 'Tutorials',
            icon: <OndemandVideoIcon />,
            link: '/tutorials',
          },
          { id: 11, title: 'Events', icon: <EventIcon />, link: '/events' },
          {
            id: 12,
            title: 'Class Sessions',
            icon: <CalendarMonthIcon />,
            link: '/class-sessions',
          },
          {
            id: 13,
            title: 'Bulk Email',
            icon: <EmailOutlinedIcon />,
            link: '/emails',
          },
          {
            id: 17,
            title: 'Push Notifications',
            icon: <NotificationsActiveIcon />,
            link: '/push-notifications',
          },
          {
            id: 14,
            title: 'Onboarding',
            icon: <SmartToyIcon />,
            link: '/onboarding-automation',
          },
          {
            id: 15,
            title: 'Community',
            icon: <ForumIcon />,
            link: '/community',
          },
          {
            id: 16,
            title: 'Tickets',
            icon: <AirplaneTicketIcon />,
            link: '/tickets',
          },
          {
            id: 20,
            title: 'HPF Assessment',
            icon: <AssignmentIcon />,
            link: '/hpf-assessment',
          },
        ],
        permissions,
        role,
        financeHolder === true
      );
    case 'ModuleLead':
      return filterSidebarByPermissions(
        [
          { id: 1, title: 'Dashboard', icon: <DashboardIcon />, link: '/' },
          {
            id: 2,
            title: 'Analytics',
            icon: <AnalyticsIcon />,
            link: '/analytics',
          },
          {
            id: 30,
            title: 'Impact Report',
            icon: <InsightsIcon />,
            link: '/impact',
          },
          {
            id: 23,
            title: 'Student Intel',
            icon: <PersonSearchIcon />,
            link: '/student-intelligence',
          },
          {
            id: 5,
            title: 'Cohorts',
            icon: <GridViewIcon />,
            link: '/cohorts',
          },
          // {
          //   id: 6,
          //   title: 'Paid Cohorts',
          //   icon: <GridViewIcon />,
          //   link: '/paid-cohorts',
          // },
          { id: 8, title: 'Groups', icon: <GroupsIcon />, link: '/groups' },
          { id: 9, title: 'Courses', icon: <BookIcon />, link: '/courses' },
          {
            id: 10,
            title: 'Tutorials',
            icon: <OndemandVideoIcon />,
            link: '/tutorials',
          },
          { id: 11, title: 'Events', icon: <EventIcon />, link: '/events' },
          {
            id: 12,
            title: 'Class Sessions',
            icon: <CalendarMonthIcon />,
            link: '/class-sessions',
          },
          {
            id: 15,
            title: 'Push Notifications',
            icon: <NotificationsActiveIcon />,
            link: '/push-notifications',
          },
          {
            id: 13,
            title: 'Community',
            icon: <ForumIcon />,
            link: '/community',
          },
          {
            id: 14,
            title: 'Tickets',
            icon: <AirplaneTicketIcon />,
            link: '/tickets',
          },
          {
            id: 16,
            title: 'HPF Assessment',
            icon: <AssignmentIcon />,
            link: '/hpf-assessment',
          },
        ],
        permissions,
        role,
        financeHolder === true
      );
    case 'Instructor':
      return filterSidebarByPermissions(
        [
          { id: 1, title: 'Dashboard', icon: <DashboardIcon />, link: '/' },
          {
            id: 2,
            title: 'Analytics',
            icon: <AnalyticsIcon />,
            link: '/analytics',
          },
          {
            id: 30,
            title: 'Impact Report',
            icon: <InsightsIcon />,
            link: '/impact',
          },
          {
            id: 23,
            title: 'Student Intel',
            icon: <PersonSearchIcon />,
            link: '/student-intelligence',
          },
          {
            id: 3,
            title: 'Cohorts',
            icon: <GridViewIcon />,
            link: '/cohorts',
          },
          // {
          //   id: 4,
          //   title: 'Paid Cohorts',
          //   icon: <GridViewIcon />,
          //   link: '/paid-cohorts',
          // },
          { id: 6, title: 'Groups', icon: <GroupsIcon />, link: '/groups' },
          { id: 7, title: 'Courses', icon: <BookIcon />, link: '/courses' },
          {
            id: 8,
            title: 'Tutorials',
            icon: <OndemandVideoIcon />,
            link: '/tutorials',
          },
          { id: 9, title: 'Events', icon: <EventIcon />, link: '/events' },
          {
            id: 10,
            title: 'Class Sessions',
            icon: <CalendarMonthIcon />,
            link: '/class-sessions',
          },
          {
            id: 11,
            title: 'Community',
            icon: <ForumIcon />,
            link: '/community',
          },
          {
            id: 12,
            title: 'Tickets',
            icon: <AirplaneTicketIcon />,
            link: '/tickets',
          },
        ],
        permissions,
        role,
        financeHolder === true
      );
    case 'AssistantInstructor':
      return filterSidebarByPermissions(
        [
          { id: 1, title: 'Dashboard', icon: <DashboardIcon />, link: '/' },
          {
            id: 2,
            title: 'Analytics',
            icon: <AnalyticsIcon />,
            link: '/analytics',
          },
          {
            id: 30,
            title: 'Impact Report',
            icon: <InsightsIcon />,
            link: '/impact',
          },
          {
            id: 23,
            title: 'Student Intel',
            icon: <PersonSearchIcon />,
            link: '/student-intelligence',
          },
          {
            id: 3,
            title: 'Cohorts',
            icon: <GridViewIcon />,
            link: '/cohorts',
          },
          // {
          //   id: 4,
          //   title: 'Paid Cohorts',
          //   icon: <GridViewIcon />,
          //   link: '/paid-cohorts',
          // },
          { id: 6, title: 'Groups', icon: <GroupsIcon />, link: '/groups' },
          { id: 7, title: 'Courses', icon: <BookIcon />, link: '/courses' },
          {
            id: 8,
            title: 'Tutorials',
            icon: <OndemandVideoIcon />,
            link: '/tutorials',
          },
          { id: 9, title: 'Events', icon: <EventIcon />, link: '/events' },
          {
            id: 10,
            title: 'Class Sessions',
            icon: <CalendarMonthIcon />,
            link: '/class-sessions',
          },
          {
            id: 11,
            title: 'Community',
            icon: <ForumIcon />,
            link: '/community',
          },
          {
            id: 12,
            title: 'Tickets',
            icon: <AirplaneTicketIcon />,
            link: '/tickets',
          },
        ],
        permissions,
        role,
        financeHolder === true
      );
    case 'DataAnalyst':
      return filterSidebarByPermissions(
        [
          { id: 1, title: 'Dashboard', icon: <DashboardIcon />, link: '/' },
          {
            id: 2,
            title: 'Analytics',
            icon: <AnalyticsIcon />,
            link: '/analytics',
          },
          {
            id: 30,
            title: 'Impact Report',
            icon: <InsightsIcon />,
            link: '/impact',
          },
          {
            id: 23,
            title: 'Student Intel',
            icon: <PersonSearchIcon />,
            link: '/student-intelligence',
          },
          {
            id: 4,
            title: 'Cohorts',
            icon: <GridViewIcon />,
            link: '/cohorts',
          },
          // {
          //   id: 5,
          //   title: 'Paid Cohorts',
          //   icon: <GridViewIcon />,
          //   link: '/paid-cohorts',
          // },
          { id: 6, title: 'Groups', icon: <GroupsIcon />, link: '/groups' },
          { id: 7, title: 'Courses', icon: <BookIcon />, link: '/courses' },
          {
            id: 8,
            title: 'Tutorials',
            icon: <OndemandVideoIcon />,
            link: '/tutorials',
          },
          { id: 9, title: 'Events', icon: <EventIcon />, link: '/events' },
          {
            id: 10,
            title: 'Class Sessions',
            icon: <CalendarMonthIcon />,
            link: '/class-sessions',
          },
          {
            id: 11,
            title: 'Community',
            icon: <ForumIcon />,
            link: '/community',
          },
          {
            id: 12,
            title: 'Tickets',
            icon: <AirplaneTicketIcon />,
            link: '/tickets',
          },
        ],
        permissions,
        role,
        financeHolder === true
      );
    case 'CommunityManager':
      return filterSidebarByPermissions(
        [
          { id: 1, title: 'Dashboard', icon: <DashboardIcon />, link: '/' },
          {
            id: 2,
            title: 'Analytics',
            icon: <AnalyticsIcon />,
            link: '/analytics',
          },
          {
            id: 30,
            title: 'Impact Report',
            icon: <InsightsIcon />,
            link: '/impact',
          },
          {
            id: 23,
            title: 'Student Intel',
            icon: <PersonSearchIcon />,
            link: '/student-intelligence',
          },
          { id: 3, title: 'Events', icon: <EventIcon />, link: '/events' },
          {
            id: 4,
            title: 'Bulk Email',
            icon: <EmailOutlinedIcon />,
            link: '/emails',
          },
          {
            id: 5,
            title: 'Community',
            icon: <ForumIcon />,
            link: '/community',
          },
          {
            id: 6,
            title: 'Tickets',
            icon: <AirplaneTicketIcon />,
            link: '/tickets',
          },
        ],
        permissions,
        role,
        financeHolder === true
      );
    case 'PartnerAdmin':
      return filterSidebarByPermissions(
        [
          { id: 1, title: 'Dashboard', icon: <DashboardIcon />, link: '/' },
          {
            id: 2,
            title: 'Analytics',
            icon: <AnalyticsIcon />,
            link: '/analytics',
          },
          {
            id: 30,
            title: 'Impact Report',
            icon: <InsightsIcon />,
            link: '/impact',
          },
          // {
          //   id: 3,
          //   title: 'Paid Cohorts',
          //   icon: <GridViewIcon />,
          //   link: '/paid-cohorts',
          // },
        ],
        permissions,
        role,
        financeHolder === true
      );
    case 'SupportAdmin':
      return filterSidebarByPermissions(
        [
          {
            id: 1,
            title: 'Analytics',
            icon: <AnalyticsIcon />,
            link: '/analytics',
          },
          {
            id: 30,
            title: 'Impact Report',
            icon: <InsightsIcon />,
            link: '/impact',
          },
          {
            id: 22,
            title: 'Student Intel',
            icon: <PersonSearchIcon />,
            link: '/student-intelligence',
          },
          {
            id: 2,
            title: 'Cohorts',
            icon: <GridViewIcon />,
            link: '/cohorts',
          },
          { id: 3, title: 'Groups', icon: <GroupsIcon />, link: '/groups' },
          { id: 4, title: 'Courses', icon: <BookIcon />, link: '/courses' },
          { id: 5, title: 'Events', icon: <EventIcon />, link: '/events' },
          {
            id: 6,
            title: 'Class Sessions',
            icon: <CalendarMonthIcon />,
            link: '/class-sessions',
          },
          {
            id: 7,
            title: 'Community',
            icon: <ForumIcon />,
            link: '/community',
          },
          {
            id: 8,
            title: 'Tickets',
            icon: <AirplaneTicketIcon />,
            link: '/tickets',
          },
        ],
        permissions,
        role,
        financeHolder === true
      );
    default:
      return filterSidebarByPermissions(
        [
          { id: 1, title: 'Dashboard', icon: <DashboardIcon />, link: '/' },
          {
            id: 2,
            title: 'Analytics',
            icon: <AnalyticsIcon />,
            link: '/analytics',
          },
          {
            id: 30,
            title: 'Impact Report',
            icon: <InsightsIcon />,
            link: '/impact',
          },
          {
            id: 22,
            title: 'Student Intel',
            icon: <PersonSearchIcon />,
            link: '/student-intelligence',
          },
          {
            id: 5,
            title: 'Administrators',
            icon: <PersonAddAltIcon />,
            link: '/users',
          },
          {
            id: 6,
            title: 'Roles & Permissions',
            icon: <ManageAccountsOutlinedIcon />,
            link: '/roles&permissions',
          },
          {
            id: 7,
            title: 'Cohorts',
            icon: <GridViewIcon />,
            link: '/cohorts',
          },
          {
            id: 2001,
            title: 'Super Students',
            icon: <AutoAwesomeIcon />,
            link: '/super-students',
          },
          // {
          //   id: 8,
          //   title: 'Paid Cohorts',
          //   icon: <GridViewIcon />,
          //   link: '/paid-cohorts',
          // },
          { id: 10, title: 'Groups', icon: <GroupsIcon />, link: '/groups' },
          { id: 11, title: 'Courses', icon: <BookIcon />, link: '/courses' },
          {
            id: 12,
            title: 'Tutorials',
            icon: <OndemandVideoIcon />,
            link: '/tutorials',
          },
          { id: 13, title: 'Events', icon: <EventIcon />, link: '/events' },
          {
            id: 14,
            title: 'Class Sessions',
            icon: <CalendarMonthIcon />,
            link: '/class-sessions',
          },
          {
            id: 15,
            title: 'Bulk Email',
            icon: <EmailOutlinedIcon />,
            link: '/emails',
          },
          {
            id: 19,
            title: 'Push Notifications',
            icon: <NotificationsActiveIcon />,
            link: '/push-notifications',
          },
          {
            id: 16,
            title: 'Onboarding',
            icon: <SmartToyIcon />,
            link: '/onboarding-automation',
          },
          {
            id: 17,
            title: 'Community',
            icon: <ForumIcon />,
            link: '/community',
          },
          {
            id: 18,
            title: 'Tickets',
            icon: <AirplaneTicketIcon />,
            link: '/tickets',
          },
          {
            id: 21,
            title: 'HPF Assessment',
            icon: <AssignmentIcon />,
            link: '/hpf-assessment',
          },
        ],
        permissions,
        role,
        financeHolder === true
      );
  }
};

export default useSidebarData;
